import { useState, useRef, useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import { number, string } from 'prop-types';
import Slick from 'react-slick';
import Button from '@alltrails/shared/denali/components/Button';
import DesktopFeatures from './DesktopFeatures';
import * as styles from './styles/styles.module.scss';
import starRating from './star_rating.png';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { toATLocalString } from '../../../utils/number_util';
import { DESKTOP_BREAKPOINT, MEDIA_QUERY_SMALL_DOWN } from '../../../utils/constants/breakpoints';
import exploreWithConfidenceW2800JpgSrc from './explore-with-confidence-w2800.jpg';
import exploreWithConfidenceW2800AvifSrc from './explore-with-confidence-w2800.avif';
import exploreWithConfidenceW768JpgSrc from './explore-with-confidence-w768.jpg';
import exploreWithConfidenceW768AvifSrc from './explore-with-confidence-w768.avif';
import exploreWithConfidenceW768WebpSrc from './explore-with-confidence-w768.webp';
import exploreWithConfidenceW2800WebpSrc from './explore-with-confidence-w2800.webp';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { defineMessages } from 'react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Slider from 'react-slick';

const EXPLORE_AND_DISCOVER_STRINGS = defineMessages({
  EDITORS_CHOICE: {
    defaultMessage: 'Recipient of the App Store Editors’ Choice award'
  },
  EXPLORE_WITH: {
    defaultMessage: 'Explore with'
  },
  CONFIDENCE: {
    defaultMessage: 'confidence'
  },
  INSPIRATION: {
    defaultMessage: 'Inspiration and guidance for wherever your trail may lead.'
  },
  SEARCH_DISCOVER_NAV: {
    defaultMessage: 'Search & discover'
  },
  NAVIGATE_TRACK_NAV: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_NAV: {
    defaultMessage: 'Remember & inspire'
  },
  SEARCH_DISCOVER_TITLE: {
    defaultMessage: 'Pick the right trail for your day'
  },
  NAVIGATE_TRACK_TITLE: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_TITLE: {
    defaultMessage: 'Keep track of your travels'
  },
  SEARCH_DISCOVER_TEXT: {
    defaultMessage: 'All our trails are verified by experts and reviewed by our global community of adventurers like you.'
  },
  NAVIGATE_TRACK_TEXT: {
    defaultMessage: 'Out on the trail, you can keep an eye on your planned route, along with elevation changes and waypoints.'
  },
  REMEMBER_INSPIRE_TEXT: {
    defaultMessage: 'Log all your trails in one place, and share insights to inspire and inform fellow adventurers.'
  },
  EXPLORE_WITH_CONFIDENCE_ALT: {
    defaultMessage: 'Two cyclists walking their bikes across a wooden bridge.'
  },
  SIGNUP: {
    defaultMessage: 'Sign up'
  }
});

const CDN_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/explore_and_discover/';

type Props = {
  windowWidth: number;
  languageRegionCode?: LanguageRegionCode;
};

const ExploreAndDiscover = ({ windowWidth, languageRegionCode = LanguageRegionCode.English }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: {
      EDITORS_CHOICE,
      EXPLORE_WITH,
      CONFIDENCE,
      INSPIRATION,
      SEARCH_DISCOVER_NAV,
      NAVIGATE_TRACK_NAV,
      REMEMBER_INSPIRE_NAV,
      SEARCH_DISCOVER_TITLE,
      NAVIGATE_TRACK_TITLE,
      REMEMBER_INSPIRE_TITLE,
      SEARCH_DISCOVER_TEXT,
      NAVIGATE_TRACK_TEXT,
      REMEMBER_INSPIRE_TEXT,
      EXPLORE_WITH_CONFIDENCE_ALT,
      SIGNUP
    }
  } = useFormatMessage(EXPLORE_AND_DISCOVER_STRINGS);
  const isDesktop = useMemo(() => {
    return windowWidth && windowWidth >= DESKTOP_BREAKPOINT;
  }, [windowWidth]);

  const SLIDE_NAVS = [SEARCH_DISCOVER_NAV, NAVIGATE_TRACK_NAV, REMEMBER_INSPIRE_NAV];
  const SLIDE_TITLES = [SEARCH_DISCOVER_TITLE, NAVIGATE_TRACK_TITLE, REMEMBER_INSPIRE_TITLE];
  const SLIDE_TEXTS = [SEARCH_DISCOVER_TEXT, NAVIGATE_TRACK_TEXT, REMEMBER_INSPIRE_TEXT];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const carouselRef = useRef<Slider>();

  const navText = SLIDE_NAVS[currentSlide];
  const title = SLIDE_TITLES[currentSlide];
  const text = SLIDE_TEXTS[currentSlide];
  const carouselLength = 3;

  const slickSettings = {
    afterChange: (index: number) => {
      setCurrentSlide(index);
      setOpacity(1);
    },
    arrows: false,
    beforeChange: () => {
      setOpacity(0);
    }
  };

  const renderDots = () => {
    return (
      <div className={styles.dots}>
        {[...Array(carouselLength)].map((_, i) => (
          <button
            key={SLIDE_NAVS[i]}
            type="button"
            aria-label={`${SLIDE_NAVS[i]}`}
            className={`${styles.dot} ${currentSlide === i ? styles.active : ''}`}
            onClick={() => {
              carouselRef?.current?.slickGoTo(i);
            }}
          />
        ))}
      </div>
    );
  };

  const renderMobileBottomContent = () => {
    return (
      <div className={styles.bottomContainer}>
        <div className={styles.carousel}>
          <Slick ref={carouselRef} {...slickSettings}>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img
                  alt={SEARCH_DISCOVER_NAV}
                  className={styles.slideImage}
                  src={`${CDN_BASE}homepage-screens-search_discover_${languageRegionCode}.png`}
                />
              </LazyLoad>
            </div>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img alt={NAVIGATE_TRACK_NAV} className={styles.slideImage} src={`${CDN_BASE}homepage-screens-navigate_${languageRegionCode}.png`} />
              </LazyLoad>
            </div>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img
                  alt={REMEMBER_INSPIRE_NAV}
                  className={styles.slideImage}
                  src={`${CDN_BASE}homepage-screens-remember_${languageRegionCode}.png`}
                />
              </LazyLoad>
            </div>
          </Slick>
        </div>
        {renderDots()}
        <div style={{ opacity }} className={styles.mobileContainer}>
          <h3 className={styles.nav}>{navText}</h3>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.featureText}>{text}</p>
          <div className={styles.signUpLink}>
            <Button
              text={SIGNUP}
              onClick={
                // link needs no onClick
                () => {}
              }
              linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/signup?ref=homepage_signup_exploreanddiscover', languageRegionCode) }}
              testId="explore-and-discover-signup-link"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={styles.topContainer}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW768AvifSrc} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW768JpgSrc} />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW768WebpSrc} type="image/webp" />
          <source srcSet={exploreWithConfidenceW2800AvifSrc} type="image/avif" />
          <source srcSet={exploreWithConfidenceW2800WebpSrc} type="image/webp" />
          <img alt={EXPLORE_WITH_CONFIDENCE_ALT} className={styles.img} loading="lazy" src={exploreWithConfidenceW2800JpgSrc} />
        </picture>
        <div className={styles.content}>
          <h1 className={styles.titleTop}>{EXPLORE_WITH}</h1>
          <h1 className={styles.titleBottom}>{CONFIDENCE}</h1>
          <p className={styles.text}>{INSPIRATION}</p>
          <div className={styles.badgeStarContainer}>
            <LazyLoad>
              <img
                className={styles.badgeImage}
                src={`https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice/apple-editor-badge-${languageRegionCode}.png`}
                alt={EDITORS_CHOICE}
              />
            </LazyLoad>
            <span className={styles.rating}>{toATLocalString(4.9, languageRegionCode, 1)}</span>
            <LazyLoad>
              <img alt="" src={starRating} className={styles.starsImage} />
            </LazyLoad>
          </div>
        </div>
      </div>
      {isDesktop ? <DesktopFeatures languageRegionCode={languageRegionCode} /> : renderMobileBottomContent()}
    </div>
  );
};

export default ExploreAndDiscover;
